import React, { useState } from "react";
import "./GeometricMap.css";
import AudioPlayer from "./audioPlayer/AudioPlayer";
import GeometricMapTracks from "../databases/geometricMapTracks.json";

const GeometricMap = ({ onNodeSelect }) => {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState("hinterland");
  const [fadeOut, setFadeOut] = useState(false);

  // Node positions match the SVG line endpoints (x2, y2 as percentages)
  const nodes = [
    {
      id: "hinterland",
      name: "HINTERLAND",
      x: 25,
      y: 15,
      description: [
        "Hinterland explores the forgotten spaces between civilizations - vast, untamed territories where nature reclaims what humanity has abandoned. The score blends organic textures with sparse, haunting melodies that evoke isolation and the sublime beauty of wilderness.",
        "Utilizing field recordings, processed acoustic instruments, and atmospheric synthesis, this sonic world captures the tension between the familiar and the unknown, creating an immersive soundscape that breathes with the rhythm of untouched landscapes.",
      ],
      tracks: GeometricMapTracks.slice(0, 5),
    },
    {
      id: "descent",
      name: "THE DESCENT",
      x: 85,
      y: 35,
      description: [
        "The Descent chronicles a journey into darkness - both literal and psychological. Deep, resonant drones form the foundation, punctuated by dissonant strings and metallic percussion that mirror the protagonist's psychological unraveling as they venture deeper into the abyss.",
        "The score employs unconventional techniques including prepared piano, reverse audio processing, and spectral manipulation to create a sense of gravity and inevitability, where each musical phrase pulls the listener further into shadowed depths.",
      ],
      tracks: GeometricMapTracks.slice(5, 10),
    },
    {
      id: "archipelago",
      name: "ARCHIPELAGO",
      x: 20,
      y: 80,
      description: [
        "Archipelago paints a musical portrait of scattered islands and the vast ocean between them. Fluid, undulating motifs represent the eternal movement of water, while delicate melodic fragments emerge like distant shores on the horizon - each island with its own distinct musical identity.",
        "The composition weaves together ambient soundscapes, subtle marimba patterns, and layered vocal textures to evoke both the isolation of remote islands and the connecting force of the surrounding sea, creating a contemplative meditation on distance and connection.",
      ],
      tracks: GeometricMapTracks.slice(10, 15),
    },
    {
      id: "synapse",
      name: "SYNAPSE",
      x: 75,
      y: 75,
      description: [
        "Synapse explores the intricate networks of neural connections and the spark of consciousness itself. Rapid, pulsing rhythms mimic electrical impulses firing across synapses, while crystalline electronic tones represent the clarity of thought emerging from chaotic neural activity.",
        "Built from modular synthesis, glitchy percussion, and processed orchestral samples, this score operates at the intersection of organic and digital, capturing the moment when individual neural signals coalesce into coherent thoughts, memories, and emotions.",
      ],
      tracks: GeometricMapTracks.slice(15, 20),
    },
  ];

  const handleNodeClick = (nodeId) => {
    if (nodeId !== selectedNode) {
      setFadeOut(true);
      // Trigger background fade immediately
      if (onNodeSelect) {
        onNodeSelect(nodeId);
      }
      setTimeout(() => {
        setSelectedNode(nodeId);
        setFadeOut(false);
      }, 300);
    }
  };

  const handleTrackChange = (trackDetails) => {
    // Optional: handle track change if needed
    console.log("Track changed:", trackDetails);
  };

  const currentNode = nodes.find((node) => node.id === selectedNode);

  return (
    <div className="geometric-map-wrapper">
      <div className="geometric-map-container">
        <svg
          className="connection-lines"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Central point */}
          <circle cx="50" cy="50" r="0.5" fill="var(--font-colour-content1)" />

          {/* Lines connecting to each node - two segments each */}
          {/* Hinterland: diagonal then horizontal */}
          <line
            x1="50"
            y1="50"
            x2="35"
            y2="15"
            className={`connection-line ${hoveredNode === "hinterland" ? "active" : ""}`}
            strokeWidth="0.2"
          />
          <line
            x1="35"
            y1="15"
            x2="25"
            y2="15"
            className={`connection-line ${hoveredNode === "hinterland" ? "active" : ""}`}
            strokeWidth="0.2"
          />

          {/* Descent: diagonal then horizontal */}
          <line
            x1="50"
            y1="50"
            x2="75"
            y2="35"
            className={`connection-line ${hoveredNode === "descent" ? "active" : ""}`}
            strokeWidth="0.2"
          />
          <line
            x1="75"
            y1="35"
            x2="85"
            y2="35"
            className={`connection-line ${hoveredNode === "descent" ? "active" : ""}`}
            strokeWidth="0.2"
          />

          {/* Archipelago: diagonal then horizontal */}
          <line
            x1="50"
            y1="50"
            x2="30"
            y2="80"
            className={`connection-line ${hoveredNode === "archipelago" ? "active" : ""}`}
            strokeWidth="0.2"
          />
          <line
            x1="30"
            y1="80"
            x2="20"
            y2="80"
            className={`connection-line ${hoveredNode === "archipelago" ? "active" : ""}`}
            strokeWidth="0.2"
          />

          {/* Synapse: diagonal then horizontal */}
          <line
            x1="50"
            y1="50"
            x2="65"
            y2="75"
            className={`connection-line ${hoveredNode === "synapse" ? "active" : ""}`}
            strokeWidth="0.2"
          />
          <line
            x1="65"
            y1="75"
            x2="75"
            y2="75"
            className={`connection-line ${hoveredNode === "synapse" ? "active" : ""}`}
            strokeWidth="0.2"
          />
        </svg>

        {/* Central point visible element */}
        <div className="central-point"></div>

        {/* Nodes */}
        {nodes.map((node) => (
          <div
            key={node.id}
            className={`map-node ${hoveredNode === node.id ? "hovered" : ""}`}
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              transform: "translate(-50%, -6px)",
            }}
            onMouseEnter={() => setHoveredNode(node.id)}
            onMouseLeave={() => setHoveredNode(null)}
            onClick={() => handleNodeClick(node.id)}
          >
            <div className="node-dot"></div>
            <div className="node-label">{node.name}</div>
          </div>
        ))}
      </div>

      {/* Content block */}
      <div className={`node-content-block ${fadeOut ? "fade-out" : "fade-in"}`}>
        <h2 className="node-content-title">{currentNode.name}</h2>

        {/* Audio Player */}
        <div className="node-audio-player">
          <AudioPlayer
            trackArray={currentNode.tracks}
            folderPath=""
            multiTrack="true"
            onTrackChange={handleTrackChange}
          />
        </div>

        <div className="node-content-text">
          <p className="paragraph1">{currentNode.description[0]}</p>
          <p className="paragraph1">{currentNode.description[1]}</p>
        </div>
      </div>
    </div>
  );
};

export default GeometricMap;
